import React, { useMemo, useId, useRef, useState, useCallback, useEffect } from 'react';

export const PRICE_RANGE_PRESETS = [
    { id: '1M', label: '1 month' },
    { id: '6M', label: '6 months' },
    { id: 'YTD', label: 'YTD' },
    { id: '1Y', label: '1 year' },
    { id: '5Y', label: '5 years' },
    { id: 'ALL', label: 'All available' },
];

function parseISODate(s) {
    const [y, m, d] = String(s).split('-').map(Number);
    return new Date(y, m - 1, d);
}

function formatISODate(d) {
    const y = d.getFullYear();
    const mo = String(d.getMonth() + 1).padStart(2, '0');
    const da = String(d.getDate()).padStart(2, '0');
    return `${y}-${mo}-${da}`;
}

/** Human-readable calendar date for axis legends (locale-aware). */
function formatChartLegendDate(iso) {
    const d = parseISODate(iso);
    return d.toLocaleDateString(undefined, {
        weekday: 'short',
        year: 'numeric',
        month: 'short',
        day: 'numeric',
    });
}

function makePriceFormatter(currency) {
    const code = currency && String(currency).trim().length === 3 ? String(currency).trim().toUpperCase() : null;
    return v => {
        if (code) {
            try {
                return new Intl.NumberFormat(undefined, {
                    style: 'currency',
                    currency: code,
                    minimumFractionDigits: 2,
                    maximumFractionDigits: 2,
                }).format(v);
            } catch {
                /* fall through */
            }
        }
        return new Intl.NumberFormat(undefined, {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2,
        }).format(v);
    };
}

/** Oldest→newest quote rows; returns rows with date >= cutoff (inclusive). */
export function filterQuotesByPriceRange(quotes, preset) {
    if (!quotes?.length) return [];
    if (preset === 'ALL') return quotes.slice();

    const anchorStr = quotes[quotes.length - 1].date;
    const anchor = parseISODate(anchorStr);
    let cutoff;

    switch (preset) {
        case '1M': {
            const d = new Date(anchor);
            d.setMonth(d.getMonth() - 1);
            cutoff = formatISODate(d);
            break;
        }
        case '6M': {
            const d = new Date(anchor);
            d.setMonth(d.getMonth() - 6);
            cutoff = formatISODate(d);
            break;
        }
        case 'YTD':
            cutoff = `${anchor.getFullYear()}-01-01`;
            break;
        case '1Y': {
            const d = new Date(anchor);
            d.setFullYear(d.getFullYear() - 1);
            cutoff = formatISODate(d);
            break;
        }
        case '5Y': {
            const d = new Date(anchor);
            d.setFullYear(d.getFullYear() - 5);
            cutoff = formatISODate(d);
            break;
        }
        default:
            return quotes.slice();
    }

    return quotes.filter(r => r.date >= cutoff);
}

const W = 880;
const H = 280;
const padL = 72;
const padR = 20;
const padT = 30;
const padB = 56;
const Y_TICKS = 5;

/**
 * @param {{ date: string, closing: number }[]} quotes oldest first
 * @param {string} [currency] ISO 4217 for close value legend (e.g. CAD, USD)
 * @param {'price'|'dividend'} [variant] Controls axis labels (default price).
 */
export default function StockPriceChart({ quotes, currency, variant = 'price' }) {
    const gradId = `stockChartFill-${useId().replace(/:/g, '')}`;

    const geom = useMemo(() => {
        if (!quotes?.length) return null;
        const fmtPrice = makePriceFormatter(currency);
        const closes = quotes.map(q => Number(q.closing));
        const minP = Math.min(...closes);
        const maxP = Math.max(...closes);
        const span = maxP - minP || 1;
        const n = quotes.length;
        const iw = W - padL - padR;
        const ih = H - padT - padB;

        const pts = quotes.map((q, i) => {
            const x = padL + (n === 1 ? iw / 2 : (i / (n - 1)) * iw);
            const y = padT + ih - ((Number(q.closing) - minP) / span) * ih;
            return { x, y };
        });

        const lineD = pts
            .map((p, i) => `${i === 0 ? 'M' : 'L'}${p.x.toFixed(2)},${p.y.toFixed(2)}`)
            .join('');

        const areaD =
            n > 0
                ? `${lineD} L${pts[pts.length - 1].x.toFixed(2)},${padT + ih} L${pts[0].x.toFixed(2)},${padT + ih} Z`
                : '';

        const yTicks = [];
        for (let i = 0; i < Y_TICKS; i++) {
            const t = minP + (span * i) / (Y_TICKS - 1);
            const fr = i / (Y_TICKS - 1);
            const y = padT + ih - fr * ih;
            yTicks.push({ value: t, y, label: fmtPrice(t) });
        }

        let xIndices;
        if (n <= 1) xIndices = [0];
        else if (n <= 8) xIndices = [0, n - 1];
        else xIndices = [0, Math.floor((n - 1) / 2), n - 1];

        const xMarks = xIndices.map(idx => ({
            idx,
            x: pts[idx].x,
            long: formatChartLegendDate(quotes[idx].date),
        }));

        const plotPts = quotes.map((q, i) => ({
            x: pts[i].x,
            y: pts[i].y,
            date: q.date,
            closing: Number(q.closing),
        }));

        return {
            lineD,
            areaD,
            firstDate: quotes[0].date,
            lastDate: quotes[quotes.length - 1].date,
            yTicks,
            xMarks,
            plotPts,
        };
    }, [quotes, currency]);

    const svgRef = useRef(null);
    const wrapRef = useRef(null);
    const [hover, setHover] = useState(null);

    useEffect(() => {
        setHover(null);
    }, [quotes]);

    const fmtPrice = useMemo(() => makePriceFormatter(currency), [currency]);

    const updateHoverFromEvent = useCallback(
        e => {
            const svg = svgRef.current;
            const wrap = wrapRef.current;
            if (!svg || !wrap || !geom?.plotPts?.length) return;
            const ctm = svg.getScreenCTM();
            if (!ctm) return;
            const pt = svg.createSVGPoint();
            pt.x = e.clientX;
            pt.y = e.clientY;
            const loc = pt.matrixTransform(ctm.inverse());
            const iw = W - padL - padR;
            const frac = iw > 0 ? (loc.x - padL) / iw : 0;
            const clamped = Math.max(0, Math.min(1, frac));
            const n = geom.plotPts.length;
            const idx = n <= 1 ? 0 : Math.round(clamped * (n - 1));

            const plotPt = geom.plotPts[idx];
            const anchor = svg.createSVGPoint();
            anchor.x = plotPt.x;
            anchor.y = plotPt.y;
            const screenCtm = svg.getScreenCTM();
            if (!screenCtm) return;
            const screenPt = anchor.matrixTransform(screenCtm);
            const wrapRect = wrap.getBoundingClientRect();
            setHover({
                idx,
                tipLeft: screenPt.x - wrapRect.left,
                tipTop: screenPt.y - wrapRect.top,
            });
        },
        [geom],
    );

    const clearHover = useCallback(() => setHover(null), []);

    if (!geom) return null;

    const ariaStart = formatChartLegendDate(geom.firstDate);
    const ariaEnd = formatChartLegendDate(geom.lastDate);
    const isDividend = variant === 'dividend';
    const yCaption = isDividend ? 'Dividend per share' : 'Closing price';
    const footnote = isDividend
        ? 'Date (horizontal) · Dividend (vertical)'
        : 'Date (horizontal) · Close / value (vertical)';

    const hoverPt =
        hover != null && geom.plotPts[hover.idx] ? geom.plotPts[hover.idx] : null;

    return (
        <div className="stock-price-chart-wrap" ref={wrapRef}>
            <span className="stock-chart-hover-aria" aria-live="polite">
                {hoverPt
                    ? `${formatChartLegendDate(hoverPt.date)}, ${fmtPrice(hoverPt.closing)}`
                    : ''}
            </span>
            <svg
                ref={svgRef}
                className="stock-price-chart-svg"
                viewBox={`0 0 ${W} ${H}`}
                preserveAspectRatio="xMidYMid meet"
                role="img"
                aria-label={`${yCaption} from ${ariaStart} through ${ariaEnd}`}
            >
                <defs>
                    <linearGradient id={gradId} x1="0" y1="0" x2="0" y2="1">
                        <stop offset="0%" stopColor="var(--primary-dark)" stopOpacity="0.18" />
                        <stop offset="100%" stopColor="var(--primary-dark)" stopOpacity="0" />
                    </linearGradient>
                </defs>

                <text x={padL} y={16} className="stock-chart-axis-title">
                    {yCaption}
                    {currency ? ` (${String(currency).trim().toUpperCase()})` : ''}
                </text>

                <g className="stock-chart-grid" aria-hidden="true">
                    {geom.yTicks.map((t, yi) => (
                        <line
                            key={`grid-${yi}`}
                            x1={padL}
                            y1={t.y}
                            x2={W - padR}
                            y2={t.y}
                            className="stock-chart-grid-line"
                        />
                    ))}
                </g>

                {geom.yTicks.map((t, yi) => (
                    <text
                        key={`ylabel-${yi}`}
                        x={padL - 8}
                        y={t.y}
                        textAnchor="end"
                        dominantBaseline="middle"
                        className="stock-chart-axis-tick stock-chart-axis-tick--value"
                    >
                        {t.label}
                    </text>
                ))}

                <path d={geom.areaD} fill={`url(#${gradId})`} />
                <path
                    d={geom.lineD}
                    fill="none"
                    stroke="var(--primary-dark)"
                    strokeWidth="2"
                    strokeLinejoin="round"
                    strokeLinecap="round"
                />

                <g className="stock-chart-x-legend">
                    {geom.xMarks.length === 1 ? (
                        <text
                            x={W / 2}
                            y={H - 28}
                            textAnchor="middle"
                            className="stock-chart-axis-tick stock-chart-axis-tick--date"
                        >
                            {geom.xMarks[0].long}
                        </text>
                    ) : (
                        geom.xMarks.map((m, i) => {
                            const isFirst = i === 0;
                            const isLast = i === geom.xMarks.length - 1;
                            const anchor = isFirst ? 'start' : isLast ? 'end' : 'middle';
                            const x = isFirst ? padL : isLast ? W - padR : m.x;
                            return (
                                <text
                                    key={m.idx}
                                    x={x}
                                    y={H - 36}
                                    textAnchor={anchor}
                                    className="stock-chart-date-block"
                                >
                                    <tspan x={x} dy="0" className="stock-chart-axis-legend-note">
                                        {isFirst ? 'Start' : isLast ? 'End' : 'Mid'}
                                    </tspan>
                                    <tspan x={x} dy="13" className="stock-chart-axis-tick stock-chart-axis-tick--date">
                                        {m.long}
                                    </tspan>
                                </text>
                            );
                        })
                    )}
                </g>

                <text x={W / 2} y={H - 6} textAnchor="middle" className="stock-chart-axis-footnote">
                    {footnote}
                </text>

                <rect
                    className="stock-chart-interaction-layer"
                    x={padL}
                    y={padT}
                    width={W - padL - padR}
                    height={H - padT - padB}
                    fill="transparent"
                    cursor="crosshair"
                    onMouseMove={updateHoverFromEvent}
                    onMouseLeave={clearHover}
                />

                {hoverPt && (
                    <g className="stock-chart-hover" aria-hidden="true">
                        <line
                            x1={hoverPt.x}
                            y1={padT}
                            x2={hoverPt.x}
                            y2={H - padB}
                            className="stock-chart-hover-line"
                        />
                        <circle
                            cx={hoverPt.x}
                            cy={hoverPt.y}
                            r={5}
                            className="stock-chart-hover-dot"
                        />
                    </g>
                )}
            </svg>
            {hoverPt && hover != null && (
                <div
                    className="stock-chart-tooltip"
                    style={{
                        left: hover.tipLeft,
                        top: hover.tipTop,
                    }}
                >
                    <div className="stock-chart-tooltip-date">{formatChartLegendDate(hoverPt.date)}</div>
                    <div className="stock-chart-tooltip-value">{fmtPrice(hoverPt.closing)}</div>
                </div>
            )}
        </div>
    );
}
